/**
 * pdfFilenameParser.js
 *
 * Attempts to extract Job Name, Piece Mark (PMK), and Revision from a PDF
 * filename using common steel-fabrication naming conventions.
 *
 * Supported patterns (most specific → least specific):
 *   JobName_PMK_RevX.pdf        →  job="JobName"  pmk="PMK"  rev="X"
 *   JobName-PMK-RevA.pdf        →  same with dashes
 *   JobName_PMK.pdf             →  job="JobName"  pmk="PMK"  rev=null
 *   PMK_RevA.pdf                →  job=null       pmk="PMK"  rev="A"
 *   PMK.pdf                     →  job=null       pmk="PMK"  rev=null
 *   Anything (1).pdf            →  fallback: raw name as pmk
 *
 * Returns { job, pmk, rev, confidence } where confidence is "high"|"medium"|"low"
 */

// Strip extension, trailing copy-numbers like " (1)", whitespace
function cleanFilename(filename) {
  return filename
    .replace(/\.pdf$/i, "")
    .replace(/\s*\(\d+\)\s*$/, "")
    .trim();
}

// Revision pattern: Rev, REV, rev followed by optional separator then alphanumeric
const REV_RE = /[_\- ]?[Rr][Ee][Vv][_\- .]?([A-Za-z0-9]+)$/;

// Common piece-mark shapes: 1GC1, B1, W12, C3A, MC-1, PL2, etc.
const PMK_RE = /^[A-Za-z]{0,4}\d+[A-Za-z]?\d*$/;

export default function parsePdfFilename(filename) {
  const base = cleanFilename(filename);
  if (!base) return { job: null, pmk: filename, rev: null, confidence: "low" };

  // Extract and strip revision first
  let rev = null;
  let remainder = base;
  const revMatch = remainder.match(REV_RE);
  if (revMatch) {
    rev = revMatch[1];
    remainder = remainder.slice(0, revMatch.index).replace(/[_\- ]+$/, "");
  }

  // Split on underscores, dashes, or spaces (common delimiters)
  const parts = remainder.split(/[_\-\s]+/).filter(Boolean);

  if (parts.length >= 3) {
    // Pattern: JobName_SubPart_PMK  or  JobName_PMK_Extra
    // Heuristic: last part that looks like a piece mark is the PMK
    const lastPart = parts[parts.length - 1];
    const jobParts = parts.slice(0, -1);
    return {
      job: jobParts.join(" "),
      pmk: lastPart,
      rev,
      confidence: "high",
    };
  }

  if (parts.length === 2) {
    // Two segments — could be Job_PMK or PMK_Something
    const [a, b] = parts;
    // If second part looks like a piece mark, treat first as job
    if (PMK_RE.test(b)) {
      return { job: a, pmk: b, rev, confidence: "high" };
    }
    // If first part looks like a piece mark, treat second as descriptor
    if (PMK_RE.test(a)) {
      return { job: b, pmk: a, rev, confidence: "medium" };
    }
    // Default: first = job, second = pmk
    return { job: a, pmk: b, rev, confidence: "medium" };
  }

  if (parts.length === 1) {
    // Single segment — probably just the PMK
    return {
      job: null,
      pmk: parts[0],
      rev,
      confidence: rev ? "medium" : "low",
    };
  }

  return { job: null, pmk: base, rev: null, confidence: "low" };
}

/**
 * Batch-parse an array of File objects.
 * Returns [{ file, job, pmk, rev, confidence }]
 */
export function parsePdfBatch(files) {
  return files.map((file) => ({
    file,
    ...parsePdfFilename(file.name),
  }));
}
